/**
 * Formats a first + last name into a full name string.
 * Handles null/undefined parts — returns an empty string if both are absent.
 */
export function formatName(person: {
  firstName?: string | null
  lastName?: string | null
}): string {
  return [person.firstName, person.lastName].filter(Boolean).join(' ')
}

/** Maps a billing interval + count to a human-readable label (e.g. "Monthly", "Every 6 months", "Yearly"). */
export function formatInterval(interval: string, intervalCount: number | null | undefined): string {
  const labels: Record<string, string> = {
    month: 'Monthly',
    year: 'Yearly',
    week: 'Weekly',
    day: 'Daily',
  }
  if (intervalCount && intervalCount > 1) {
    if (interval === 'month' && intervalCount === 6) return 'Biannually'
    return `Every ${intervalCount} ${interval}s`
  }
  return labels[interval] ?? interval
}

/**
 * Advances a date by whole calendar months, clamping the day-of-month to the
 * last valid day of the target month (e.g. Jan 31 + 1 month -> Feb 28, not Mar 3).
 */
function addMonths(date: Date, months: number): Date {
  const result = new Date(date)
  result.setDate(1)
  result.setMonth(result.getMonth() + months)
  const daysInMonth = new Date(result.getFullYear(), result.getMonth() + 1, 0).getDate()
  result.setDate(Math.min(date.getDate(), daysInMonth))
  return result
}

/**
 * Calculates a human-readable age string from an ISO date of birth.
 *
 * Patients under 1 year old are shown as months and weeks (e.g. "3 months
 * 2 weeks"); from 1 year onwards the age is shown as years and months
 * (e.g. "3 years 4 months"). `< 1 week` is the minimum age threshold.
 *
 * Pass `{ short: true }` for a compact form suitable for tight layouts
 * (e.g. "3 yrs 4 mo" instead of "3 years 4 months"), and `asOf` for the age on a
 * past day rather than today.
 */
export function formatAge(dateOfBirth: string, options?: { short?: boolean; asOf?: Date }): string {
  const short = options?.short ?? false
  const format = (
    count: number,
    long: string,
    shortSingular: string,
    shortPlural = shortSingular
  ) => {
    const label = short
      ? count === 1
        ? shortSingular
        : shortPlural
      : count === 1
        ? long
        : `${long}s`
    return `${count} ${label}`
  }

  const now = options?.asOf ?? new Date()
  const dob = new Date(dateOfBirth)
  let years = now.getFullYear() - dob.getFullYear()
  let months = now.getMonth() - dob.getMonth()
  if (now.getDate() < dob.getDate()) months--
  if (months < 0) {
    years--
    months += 12
  }

  if (years <= 0) {
    const msPerWeek = 1000 * 60 * 60 * 24 * 7
    const weeks = Math.floor((now.getTime() - addMonths(dob, months).getTime()) / msPerWeek)

    if (months <= 0 && weeks <= 0) return short ? '< 1 wk' : '< 1 week'

    const parts: string[] = []
    if (months > 0) parts.push(format(months, 'month', 'mo'))
    if (weeks > 0) parts.push(format(weeks, 'week', 'wk'))
    return parts.join(' ')
  }

  const parts: string[] = []
  if (years > 0) parts.push(format(years, 'year', 'yr', 'yrs'))
  if (months > 0) parts.push(format(months, 'month', 'mo'))
  return parts.join(' ')
}

const ONES = [
  'zero',
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
  'ten',
  'eleven',
  'twelve',
  'thirteen',
  'fourteen',
  'fifteen',
  'sixteen',
  'seventeen',
  'eighteen',
  'nineteen',
]
const TENS = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety']

/**
 * Converts a non-negative integer (0–999) to its English word form.
 * Used on written prescriptions where regulations require numbers in words.
 */
export function numberToWords(n: number): string {
  if (n < 0 || n > 999 || !Number.isInteger(n)) return String(n)
  if (n < 20) return ONES[n]
  if (n < 100) {
    const remainder = n % 10
    return TENS[Math.floor(n / 10)] + (remainder ? `-${ONES[remainder]}` : '')
  }
  const hundreds = `${ONES[Math.floor(n / 100)]} hundred`
  const remainder = n % 100
  return remainder ? `${hundreds} and ${numberToWords(remainder)}` : hundreds
}

/** Known irregular unit plurals for veterinary dispensing. Falls back to appending "s". */
const IRREGULAR_UNIT_PLURALS: Record<string, string> = {
  box: 'boxes',
  // Invariant units
  ml: 'ml',
  mg: 'mg',
  g: 'g',
  kg: 'kg',
  l: 'l',
  each: 'each',
}

/**
 * Pluralizes a dispensing unit label based on quantity.
 * Uses a static lookup for known units, falls back to appending "s".
 * Returns "unit"/"units" when the unit is null or empty.
 */
export function pluralizeUnit(unit: string | null, quantity: number): string {
  const label = unit?.trim() || 'unit'
  if (quantity === 1) return label
  return IRREGULAR_UNIT_PLURALS[label.toLowerCase()] ?? `${label}s`
}

/** Title-cases a string, replacing dashes/underscores with spaces (e.g. "sick-unwell" → "Sick Unwell"). */
export function titleCase(value: string): string {
  return value.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

export function possessive(name: string): string {
  return `${name}${/s$/i.test(name) ? "'" : "'s"}`
}

/** Masks the local part of an email for logging, e.g. "jane@example.com" → "j***@example.com". */
export function maskEmail(email: string): string {
  const [local, domain] = email.split('@')
  if (!domain) return '***'
  return `${local.slice(0, 1)}***@${domain}`
}

/** Masks the middle of a phone number for logging, keeping the first 3 and last 2 chars, e.g. "+447700900123" → "+44***23". */
export function maskPhone(phone: string): string {
  if (phone.length <= 5) return '***'
  return `${phone.slice(0, 3)}***${phone.slice(-2)}`
}

/** Converts a name to a slug (lowercase, alphanumeric + hyphens). */
export function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

/** Truncates text to a maximum length, appending an ellipsis when content was removed. */
export function truncateText(text: string, maxLength = 300): string {
  if (text.length <= maxLength) return text
  return `${text.slice(0, maxLength)}…`
}

/** Formats a quantity for display — rounds and strips trailing zeros (e.g. 12.50 → "12.5", 3.00 → "3"). */
export function formatQuantity(value: number, dp = 2): string {
  return parseFloat(value.toFixed(dp)).toString()
}

/** Formats a duration in minutes as "30 min" or "1h 15m". */
export function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes} min`
  const hours = Math.floor(minutes / 60)
  const remainder = minutes % 60
  return remainder ? `${hours}h ${remainder}m` : `${hours}h`
}

/** Formats elapsed time since a date or ISO string as "< 1 min", "X min", or "Xh Ym". */
export function formatElapsedTime(since: Date | string): string {
  const date = typeof since === 'string' ? new Date(since) : since
  const mins = Math.floor((Date.now() - date.getTime()) / 60000)
  if (mins < 1) return '< 1 min'
  if (mins < 60) return `${mins} min`
  const hrs = Math.floor(mins / 60)
  return `${hrs}h ${mins % 60}m`
}
